import * as breakpoint from '@pixelunion/breakpoint';
import AsyncView from 'shopify-asyncview';
import debounce from 'just-debounce';
import rimg from '@pixelunion/rimg-shopify';
import ScrollLock from '../utils/ScrollLock';

export default class LiveSearch {
  constructor(el, options = {}) {
    this.el = el;
    this.options = options;
    this.resultsStyleStale = true;
    this.resultspositionStale = true;

    this.stopProp = e => e.stopPropagation();
    this.closeEsc = (e) => {
      if (e.key === 'Escape') {
        this.stopProp(e);
        this._close();
      }
    };
    this.cancelEnt = (e) => {
      if (e.key === 'Enter' && e.currentTarget.value === '') {
        e.preventDefault();
        this.stopProp(e);
      }
    };
    this.open = (e) => {
      this.stopProp(e);
      this._open();
    };
    this.close = (e) => {
      this.stopProp(e);
      this._close();
    };
    this.search = (e) => {
      this.stopProp(e);
      this._search();
    };
    this.setResultspositionStyleStale = () => {
      this.resultspositionStale = true;
      this.resultsStyleStale = true;
    };
    this.redirect = (e) => {
      this._redirect(e);
    };
    this.setResultspositionStale = () => { this.resultspositionStale = true; };
    this.setResultsStyleStale = () => { this.resultsStyleStale = true; };
    this.searchDebounced = debounce(this._search.bind(this), 500);

    this.minimumCharactersBeforeSearch = 3;

    this.liveSearchForm = this.el.querySelector('[data-live-search-form]');
    this.liveSearchFormInput = this.el.querySelector('[data-live-search-form-input]');
    this.liveSearchResults = this.el.querySelector('[data-live-search-results]');
    this.liveSearchResultsProductContent = this.el.querySelector('[data-live-search-results-product-content]');
    this.liveSearchResultsPostPagesContent = this.el.querySelector('[data-live-search-results-postpages-content]');
    this.liveSearchResultsFooter = this.el.querySelector('[data-live-search-results-footer]');
    this.liveSearchResultsFooterViewAll = this.el.querySelector('[data-live-search-results-footer-viewall]');

    this.liveSearchResultsProductContentPlaceholder = this.liveSearchResultsProductContent.innerHTML;
    this.liveSearchResultsPostPagesContentPlaceholder = this.liveSearchResultsPostPagesContent ? this.liveSearchResultsPostPagesContent.innerHTML : '';

    this.liveSearchButtonSearch = this.el.querySelector('[data-live-search-button-search]');
    this.liveSearchFormButtonSearch = this.el.querySelector('[data-live-search-form-button-search]');
    this.liveSearchFormButtonClose = this.el.querySelector('[data-live-search-form-button-close]');

    this.previousLiveSearchFormInputValue = '';

    this.liveSearchForm.addEventListener('click', this.stopProp);
    this.liveSearchForm.addEventListener('focusin', this.stopProp);
    this.liveSearchButtonSearch.addEventListener('click', this.open);
    this.liveSearchFormButtonClose.addEventListener('click', this.close);
    this.liveSearchFormButtonSearch.addEventListener('click', this.redirect);

    if (this.options.enabled) {
      this.liveSearchFormInput.addEventListener('focusin', this.search);
      this.liveSearchFormInput.addEventListener('keydown', this.cancelEnt);
      this.liveSearchResults.addEventListener('click', this.stopProp);
      this.liveSearchResults.addEventListener('focusin', this.stopProp);
    }

    this.liveSearchFormButtonClose.tabIndex = -1;
    this.liveSearchFormButtonSearch.tabIndex = -1;
    this.liveSearchFormInput.tabIndex = -1;
  }

  unload() {
    // Reset live search results and form
    this._close();

    // Reset live search
    this.liveSearchForm.removeEventListener('click', this.stopProp);
    this.liveSearchForm.removeEventListener('focusin', this.stopProp);
    this.liveSearchButtonSearch.removeEventListener('click', this.open);
    this.liveSearchFormButtonClose.removeEventListener('click', this.close);
    this.liveSearchFormButtonSearch.addEventListener('click', this.redirect);
    this.liveSearchFormInput.removeEventListener('focusin', this.search);
    this.liveSearchFormInput.removeEventListener('keydown', this.cancelEnt);
    this.liveSearchResults.removeEventListener('click', this.stopProp);
    this.liveSearchResults.removeEventListener('focusin', this.stopProp);

    ScrollLock.unlock();
  }

  _open() {
    window.addEventListener('keydown', this.closeEsc);
    document.body.addEventListener('click', this.close);
    document.body.addEventListener('focusin', this.close);

    document.documentElement.classList.add('live-search-visible');
    this.el.classList.remove('live-search-hidden');
    this.liveSearchFormButtonClose.tabIndex = 0;
    this.liveSearchFormButtonSearch.tabIndex = 0;
    this.liveSearchFormInput.tabIndex = 0;
    this.liveSearchFormInput.focus();

    if (breakpoint.max('M')) {
      this.liveSearchResults.addEventListener('touchmove', this.stopProp);
      this.liveSearchResults.addEventListener('scroll', this.stopProp);
      ScrollLock.lock();
    }

    if (this.options.enabled) {
      window.addEventListener('resize', this.setResultspositionStyleStale);
      window.addEventListener('scroll', this.setResultspositionStyleStale);
      this.liveSearchFormInput.addEventListener('keyup', this.searchDebounced);

      this.positionResultsContainerAnimationFrame = window.requestAnimationFrame(this._positionResultsContainer.bind(this));
      this.styleResultsContainerAnimationFrame = window.requestAnimationFrame(this._styleResultsContainer.bind(this));
    }
  }

  _close() {
    // Reset live search form
    window.removeEventListener('keydown', this.closeEsc);
    document.body.removeEventListener('click', this.close);
    document.body.removeEventListener('focusin', this.close);
    this.liveSearchResults.removeEventListener('touchmove', this.stopProp);
    this.liveSearchResults.removeEventListener('scroll', this.stopProp);
    window.removeEventListener('resize', this.setResultspositionStyleStale);
    window.removeEventListener('scroll', this.setResultspositionStyleStale);
    this.liveSearchFormInput.removeEventListener('keyup', this.searchDebounced);
    window.cancelAnimationFrame(this.positionResultsContainerAnimationFrame);
    window.cancelAnimationFrame(this.styleResultsContainerAnimationFrame);

    const liveSearchResultsReset = () => {
      // Reset live search results to initial state after it's no longer visible
      this.liveSearchResults.removeEventListener('transitionend', liveSearchResultsReset);
      this.el.classList.remove('live-search-results-placeholder');

      this.liveSearchResultsProductContent.innerHTML = this.liveSearchResultsProductContentPlaceholder;

      if (this.liveSearchResultsPostPagesContent) {
        this.liveSearchResultsPostPagesContent.innerHTML = this.liveSearchResultsPostPagesContentPlaceholder;
      }

      this.liveSearchResultsFooterViewAll.href = '';
      this.liveSearchResults.style.top = '';
      this.liveSearchResults.style.left = '';
      this.liveSearchResults.style.right = '';
      this.liveSearchResults.style.paddingBottom = '';
      this.resultspositionStale = true;
    };

    if (this.el.classList.contains('live-search-results-hidden')) {
      liveSearchResultsReset();
    } else {
      this.liveSearchResults.addEventListener('transitionend', liveSearchResultsReset);
    }

    // Close live search and live search results
    document.documentElement.classList.remove('live-search-visible');
    this.el.classList.add('live-search-results-hidden');
    this.el.classList.add('live-search-hidden');
    this.liveSearchFormButtonClose.tabIndex = -1;
    this.liveSearchFormButtonSearch.tabIndex = -1;
    this.liveSearchFormInput.tabIndex = -1;
    this.liveSearchButtonSearch.focus();
    this.previousLiveSearchFormInputValue = '';
    ScrollLock.unlock();
  }

  _positionResultsContainer() {
    if (!this.resultspositionStale) {
      this.positionResultsContainerAnimationFrame = window.requestAnimationFrame(this._positionResultsContainer.bind(this));
      return;
    }

    // Reset values on results container
    this.liveSearchResults.style.top = '';
    this.liveSearchResults.style.left = '';
    this.liveSearchResults.style.right = '';

    // position the results container
    const searchFormBoundingRect = this.liveSearchForm.getBoundingClientRect();
    const searchFormHeight = searchFormBoundingRect.height;
    const searchResultOffset = `${searchFormBoundingRect.top + searchFormHeight}px`;
    const windowWidth = document.documentElement.clientWidth;

    this.liveSearchResults.style.top = searchResultOffset;

    // If the form is closer to the left, open results on the left, otherwise open on the right
    if (searchFormBoundingRect.left <= windowWidth - searchFormBoundingRect.right) {
      this.liveSearchResults.style.left = `${searchFormBoundingRect.left}px`;
    } else {
      this.liveSearchResults.style.right = `${windowWidth - searchFormBoundingRect.right}px`;
    }

    window.removeEventListener('scroll', this.setResultspositionStyleStale);
    this.liveSearchResults.removeEventListener('touchmove', this.stopProp);
    this.liveSearchResults.removeEventListener('scroll', this.stopProp);

    if (breakpoint.min('L')) {
      window.addEventListener('scroll', this.setResultspositionStyleStale);
      ScrollLock.unlock();

      const windowHeight = document.documentElement.clientHeight;
      const headerHeight = document.querySelector('.main-header').offsetHeight;
      const maxHeight = windowHeight - headerHeight; 

      this.liveSearchResults.style.maxHeight = `${maxHeight}px`;
    } else {
      this.liveSearchResults.addEventListener('touchmove', this.stopProp);
      this.liveSearchResults.addEventListener('scroll', this.stopProp);
      ScrollLock.lock();
    }

    this.resultspositionStale = false;
    this.positionResultsContainerAnimationFrame = window.requestAnimationFrame(this._positionResultsContainer.bind(this));
  }

  _styleResultsContainer() {
    if (!this.resultsStyleStale) {
      this.styleResultsContainerAnimationFrame = window.requestAnimationFrame(this._styleResultsContainer.bind(this));
      return;
    }

    this.liveSearchResults.style.paddingBottom = '';

    if (breakpoint.is('S')) {
      // Only on mobile, allows results to scroll correct distance when overflowing
      this.liveSearchResults.style.paddingBottom = `${this.liveSearchResultsFooter.getBoundingClientRect().height}px`;
    }

    this.resultsStyleStale = false;
    this.styleResultsContainerAnimationFrame = window.requestAnimationFrame(this._styleResultsContainer.bind(this));
  }

  _search() {
    const liveSearchFormInputValue = this.liveSearchFormInput.value;

    if (this.previousLiveSearchFormInputValue === liveSearchFormInputValue) {
      return;
    }

    this.el.classList.add('live-search-results-placeholder');
    this.liveSearchResultsProductContent.innerHTML = this.liveSearchResultsProductContentPlaceholder;

    if (this.liveSearchResultsPostPagesContent) {
      this.liveSearchResultsPostPagesContent.innerHTML = this.liveSearchResultsPostPagesContentPlaceholder;
    }

    this.liveSearchResultsFooterViewAll.href = '';

    if (liveSearchFormInputValue.length >= this.minimumCharactersBeforeSearch) {
      this.el.classList.remove('live-search-results-hidden');
      this._loadResults(liveSearchFormInputValue);
    } else {
      this.el.classList.add('live-search-results-hidden');
    }

    this.previousLiveSearchFormInputValue = liveSearchFormInputValue;
  }

  _loadResults(terms) {
    AsyncView.load(
      `${window.Theme.routes.search_url}?type=${this.options.display.replace(/-/g, ',')}&q=${terms}`,
      'ajax',
      { hash: terms },
    ).done((data) => {
      this.liveSearchResultsProductContent.innerHTML = data.html.products;

      if (this.liveSearchResultsPostPagesContent) {
        this.liveSearchResultsPostPagesContent.innerHTML = data.html.postpages;
      }

      this.liveSearchResultsFooterViewAll.href = data.options.search_url;

      this.el.classList.remove('live-search-results-placeholder');

      rimg.watch(this.liveSearchResultsProductContent);

      const productsResultsCount = data.options.products_results_count;
      const postsResultsCount = data.options.posts_results_count;
      const pagesResultsCount = data.options.pages_results_count;

      let shouldDisplayFooter = false;

      switch (this.options.display) {
        case 'product-article-page':
          shouldDisplayFooter = productsResultsCount > 0 || postsResultsCount > 0 || pagesResultsCount > 0;
          break;
        case 'product-article':
          shouldDisplayFooter = productsResultsCount > 0 || postsResultsCount > 0;
          break;
        case 'product-page':
          shouldDisplayFooter = productsResultsCount > 0 || pagesResultsCount > 0;
          break;
        case 'product':
          shouldDisplayFooter = productsResultsCount > 0;
          break;
        default:
          break;
      }

      if (shouldDisplayFooter) {
        this.el.classList.remove('live-search-results-footer-hidden');
      } else {
        this.el.classList.add('live-search-results-footer-hidden');
      }

      this.resultspositionStale = true;
      this.resultsStyleStale = true;
    });
  }

  _redirect (e) {
    if (this.liveSearchFormInput.value === '') {
      e.preventDefault();
    }
  }
}
